import React, { useState, useEffect } from 'react';
import { User } from 'lucide-react';

interface PlayerAvatarProps {
  /** URL de l'avatar (fournie par le système de tournoi). Optionnelle. */
  avatarUrl?: string;
  /** Couleur du joueur (anneau + icône de fallback). */
  color: string;
  /**
   * Diamètre de l'avatar. Nombre = pixels ; chaîne = valeur CSS libre
   * (ex. `clamp(1.5rem, 10cqh, 3rem)` pour une taille fluide dans le Court).
   */
  size?: number | string;
  /** Épaisseur de l'anneau de couleur en pixels. Par défaut ~4% de `size` (numérique) ou 3px. */
  ringWidth?: number;
  /** Texte alternatif (nom du joueur). */
  alt?: string;
}

/**
 * Avatar du joueur : image circulaire entourée d'un anneau de couleur.
 *
 * - Si `avatarUrl` est fournie ET se charge correctement, affiche la photo.
 * - Sinon (absence d'URL ou erreur de chargement, ex. PWA hors-ligne ou URL
 *   morte), retombe sur l'icône `User` colorée - le comportement historique.
 *
 * L'anneau de couleur reprend la couleur du joueur, qui reste l'identifiant
 * primaire pour l'arbitre.
 */
export const PlayerAvatar: React.FC<PlayerAvatarProps> = ({
  avatarUrl,
  color,
  size = 70,
  ringWidth,
  alt = '',
}) => {
  const [hasError, setHasError] = useState(false);

  // Réinitialise l'erreur si l'URL change (nouveau match / nouveau joueur).
  useEffect(() => {
    setHasError(false);
  }, [avatarUrl]);

  const showImage = Boolean(avatarUrl) && !hasError;
  // Anneau : 4% du diamètre pour une taille numérique, sinon 3px (taille fluide CSS).
  const ring = ringWidth ?? (typeof size === 'number' ? Math.max(2, Math.round(size * 0.04)) : 3);

  return (
    <div
      className="rounded-full flex items-center justify-center overflow-hidden bg-white shrink-0"
      style={{
        width: size,
        height: size,
        border: `${ring}px solid ${color}`,
      }}
    >
      {showImage ? (
        <img
          src={avatarUrl}
          alt={alt}
          className="w-full h-full object-cover"
          onError={() => setHasError(true)}
        />
      ) : (
        // Fallback : icône dimensionnée en % du conteneur (suit `size`, fluide ou non).
        // L'icône `User` de lucide n'est pas parfaitement centrée dans son viewBox
        // (dessin légèrement décalé vers la droite et le bas) : on recentre optiquement.
        <User
          style={{ color, width: '64%', height: '64%', transform: 'translate(-3%, -6%)' }}
          fill={color}
          aria-label={alt || undefined}
        />
      )}
    </div>
  );
};
